import { createAsyncThunk } from '@reduxjs/toolkit'
import { handleError, hasError } from '@repo/shared/api/helpers'
import { SuccessResponseDTO } from '@repo/shared/dtos/generic'

import { selectCurrentFilters } from '@/store/beneficiary-list-filters/selectors'
import { CaseWorker } from '@/store/user'

import { AsyncThunkConfig, RejectValue } from '../actions'

export const assignCaseWorkerToBeneficiary = createAsyncThunk<
  SuccessResponseDTO,
  { beneficiaryId: string; caseWorkerId: string },
  AsyncThunkConfig
>(
  'ASSIGN_CASE_WORKER_TO_BENEFICIARY',
  async (
    { beneficiaryId, caseWorkerId },
    { getState, extra, rejectWithValue, fulfillWithValue },
  ) => {
    const state = getState()
    const organizationId = state.user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

    const res = await extra.altApi.caseWorkers.assignCaseWorkerToBeneficiary({
      organizationId,
      beneficiaryId,
      caseWorkerId,
    })

    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue({ success: true })
  },
)

export const assignCaseWorkerToBeneficiariesFromFilters = createAsyncThunk<
  SuccessResponseDTO,
  { caseWorkerId: string },
  AsyncThunkConfig
>(
  'ASSIGN_CASE_WORKER_TO_BENEFICIARY',
  async ({ caseWorkerId }, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const state = getState()
    const organizationId = state.user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

    const filters = selectCurrentFilters(state)
    const sortOption = state.beneficiaryList.sortOption

    const res = await extra.altApi.caseWorkers.assignCaseWorkerToBeneficiariesFromFilters({
      organizationId,
      caseWorkerId,
      filters,
      sortOption,
    })

    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue({ success: true })
  },
)

export const unassignCaseWorkerFromBeneficiariesFromFilters = createAsyncThunk<
  SuccessResponseDTO,
  { caseWorkerId: string },
  AsyncThunkConfig
>(
  'UNASSIGN_CASE_WORKER_FROM_BENEFICIARY',
  async ({ caseWorkerId }, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const state = getState()
    const organizationId = state.user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

    const filters = selectCurrentFilters(state)
    const sortOption = state.beneficiaryList.sortOption

    const res = await extra.altApi.caseWorkers.unassignCaseWorkerFromBeneficiariesFromFilters({
      organizationId,
      caseWorkerId,
      filters,
      sortOption,
    })

    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue({ success: true })
  },
)

export const unassignCaseWorkerFromBeneficiary = createAsyncThunk<
  SuccessResponseDTO,
  { beneficiaryId: string; caseWorkerId: string },
  AsyncThunkConfig
>(
  'UNASSIGN_CASE_WORKER_FROM_BENEFICIARY',
  async (
    { beneficiaryId, caseWorkerId },
    { getState, extra, rejectWithValue, fulfillWithValue },
  ) => {
    const state = getState()
    const organizationId = state.user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

    const res = await extra.altApi.caseWorkers.unassignCaseWorkerFromBeneficiary({
      organizationId,
      beneficiaryId,
      caseWorkerId,
    })

    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue({ success: true })
  },
)

export const getCaseWorkers = createAsyncThunk<
  { caseWorkers: CaseWorker[] },
  void,
  AsyncThunkConfig
>('GET_CASE_WORKERS', async (_, { getState, extra, rejectWithValue, fulfillWithValue }) => {
  const state = getState()
  const organizationId = state.user.currentOrganizationId
  if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

  const res = await extra.api.caseWorkers.get({ organizationId })
  if (hasError(res)) return rejectWithValue(handleError(res))
  return fulfillWithValue({ caseWorkers: res })
})
